import { BasicInformationDto } from '../generated-types/basicInformationDto';
import { ClientOnboardingConfigDto } from '../generated-types/clientOnboardingConfigDto';
import { ClientOnboardingPreviewDto } from '../generated-types/clientOnboardingPreviewDto';
import { ClientStepStateDto } from '../generated-types/clientStepStateDto';
import { LicenseAndPricingDto } from '../generated-types/licenseAndPricingDto';
import { LicenseTierDto } from '../generated-types/licenseTierDto';
import { OrgSetupDto } from '../generated-types/orgSetupDto';
import { StartClientOnboardingDto } from '../generated-types/startClientOnboardingDto';
import { AbstractApi } from './AbstractApi';

/**
 * An api to interact with the client onboarding controller
 */
export default class ClientOnboardingApi extends AbstractApi {
  private static configuration: ClientOnboardingConfigDto;

  /**
   * Returns the minimum number of licenses an organization needs to purchase, if the onboarding was initiated by the public.
   */
  async GetLicenseMinimum(): Promise<number> {
    const response = await fetch(this.BuildUrl('client-onboarding/minimum'), {
      method: 'GET',
    });

    if (!response.ok) throw Error();

    return (await response.text()) as unknown as number;
  }

  /**
   * Returns the minimum number of licenses an organization needs to purchase while onboarding is occurring
   */
  async GetLicenseMinimumWhileOnboarding(): Promise<number> {
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/minimum-while-onboarding'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return (await response.text()) as unknown as number;
  }

  /**
   * Start a client onboarding
   * @param startClientOnboardingDto The start client onboarding dto to begin
   * @returns The code and the body associated, if it exists
   */
  async StartClientOnboarding(
    startClientOnboardingDto: StartClientOnboardingDto
  ): Promise<{ code: number; body: string }> {
    const response = await this.authFetch(this.BuildUrl('client-onboarding'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(startClientOnboardingDto),
    });

    return {
      code: response.status,
      body: await response.text(),
    };
  }

  /**
   * Get all the client onboarding previews
   * @returns All the client onboarding preview dtos
   */
  async GetAllClientOnboarding(): Promise<ClientOnboardingPreviewDto[]> {
    const response = await this.authFetch(this.BuildUrl('client-onboarding'), {
      method: 'GET',
    });

    if (!response.ok) throw Error();

    const clientOnboardingPreviewDtos =
      (await response.json()) as unknown as ClientOnboardingPreviewDto[];

    // Convert the dates
    // - JSON doesn't have a clear way to differentiate a string versus a date,
    //  so even though we convert to an object that has dates, they're still
    //  strings behind-the-scenes. We simply can set a new date using those
    //  strings back to the matching property, to properly set them as dates
    for (const clientOnboardingPreviewDto of clientOnboardingPreviewDtos) {
      // Convert the started moment
      if (clientOnboardingPreviewDto.started) {
        clientOnboardingPreviewDto.started = new Date(
          clientOnboardingPreviewDto.started
        );
      }

      // Convert the completed moment
      if (clientOnboardingPreviewDto.completed) {
        clientOnboardingPreviewDto.completed = new Date(
          clientOnboardingPreviewDto.completed
        );
      }
    }

    return clientOnboardingPreviewDtos;
  }

  /**
   * Get all the client onboarding step for the currently logged in client.
   * @returns All the client's client onboarding step states
   */
  async GetStepStates(): Promise<ClientStepStateDto[]> {
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/step-states'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return response.json() as unknown as ClientStepStateDto[];
  }

  /**
   * Check if the client onboarding is still valid
   * @returns True, if the client onboarding is still valid. False, otherwise.
   */
  async IsValid(): Promise<Boolean> {
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/validity'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return (await response.text()) == 'true';
  }

  /**
   * Get all the license tiers
   * @returns All the license tiers that are available for the user
   */
  async GetLicenseTiers(): Promise<LicenseTierDto[]> {
    const response = await fetch(
      this.BuildUrl('client-onboarding/steps/license-and-pricing/tiers'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return response.json() as unknown as LicenseTierDto[];
  }

  /**
   * Complete the welcome step
   */
  async CompleteWelcomeStep(): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/steps/welcome'),
      {
        method: 'PUT',
      }
    );

    if (!response.ok) throw Error();
  }

  /**
   * Complete the basic information step
   * @param basicInfo The basic information dto
   */
  async CompleteBasicInformationStep(
    basicInfo: BasicInformationDto
  ): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/steps/basic-information'),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(basicInfo),
      }
    );

    if (!response.ok) throw Error();
  }

  /**
   * Complete the license and pricing step
   * @param basicInfo The license and pricing dto
   */
  async CompleteLicenseAndPricing(
    licenseAndPricingDto: LicenseAndPricingDto
  ): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/steps/license-and-pricing'),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(licenseAndPricingDto),
      }
    );

    if (!response.ok) throw Error();
  }

  /**
   * Get the basic information step that has been completed already
   * @returns The previously completed basic information dto
   */
  async GetBasicInformationStep(): Promise<BasicInformationDto> {
    // Get the basic information
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/steps/basic-information'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return response.json() as unknown as BasicInformationDto;
  }

  /**
   * Get the license and pricing step that has been completed already
   * @returns The previously completed license and pricing dto
   */
  async GetLicenseAndPricing(): Promise<LicenseAndPricingDto> {
    // Get the basic information
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/steps/license-and-pricing'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    const dto = response.json() as unknown as LicenseAndPricingDto;

    return dto;
  }

  /**
   * Get the client onboarding configuration
   * @returns The client onboarding configuration
   */
  async GetClientOnboardingConfig(): Promise<ClientOnboardingConfigDto> {
    if (!ClientOnboardingApi.configuration) {
      const response = await this.authFetch(
        this.BuildUrl('client-onboarding/configuration'),
        {
          method: 'GET',
        }
      );

      if (!response.ok) throw Error();

      ClientOnboardingApi.configuration =
        response.json() as unknown as ClientOnboardingConfigDto;
    }

    return ClientOnboardingApi.configuration;
  }

  /**
   * Get the master service agreement pdf for the currently authenticated user who is going
   *  through client onboarding
   * @returns The blob path to the downloaded pdf
   */
  async GetMasterServiceAgreementPdf(): Promise<string> {
    const timezoneOffset = new Date().getTimezoneOffset() / -60;
    const encodedOffset = encodeURIComponent(timezoneOffset);
    const response = await this.authFetch(
      this.BuildUrl(
        `client-onboarding/steps/master-service-agreement/pdf/${encodedOffset}`
      ),
      {
        method: 'GET',
      }
    );

    // If the response is unsuccessful, throw an error
    if (!response.ok) throw Error();

    // Process the blob
    const pdfBlob = await response.blob();
    const url = URL.createObjectURL(pdfBlob);

    // Return the url
    return url;
  }

  /**
   * Sign the master service agreement
   * @param signature The signature to sign with
   * @returns The blob path to the signed master service agreement
   */
  async SignMasterServiceAgreementPdf(signature: Blob): Promise<string> {
    const timezoneOffset = new Date().getTimezoneOffset() / -60;
    const encodedOffset = encodeURIComponent(timezoneOffset);
    const formData = new FormData();
    formData.append('signatureFile', signature);
    const response = await this.authFetch(
      this.BuildUrl(
        `client-onboarding/steps/master-service-agreement/sign/${encodedOffset}`
      ),
      {
        method: 'POST',
        body: formData,
      }
    );

    // If the response is unsuccessful, throw an error
    if (!response.ok) {
      if (response.status == 400) throw Error(await response.text());
      throw Error();
    }

    // Process the blob
    const pdfBlob = await response.blob();
    const url = URL.createObjectURL(pdfBlob);

    // Return the url
    return url;
  }

  /**
   * Get the org setup
   * @returns The org setup dto
   */
  async GetOrgSetup(): Promise<OrgSetupDto> {
    // Get the basic information
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/steps/org-setup'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    const dto = response.json() as unknown as OrgSetupDto;

    return dto;
  }

  /**
   * Complete the organization setup
   * @param orgSetupDto The org setup dto to complete the setup with
   */
  async CompleteOrgSetup(orgSetupDto: OrgSetupDto): Promise<string> {
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/steps/org-setup'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orgSetupDto),
      }
    );

    if (!response.ok) throw Error();

    return response.text();
  }

  /**
   * Mark that the user has submitted a payment
   */
  async paymentSubmitted(): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('client-onboarding/steps/payment-submitted'),
      {
        method: 'PUT',
      }
    );

    if (!response.ok) throw Error();
  }

  /**
   * Delete the client onboarding
   * @param onboardingId The id of the client onboarding to delete
   */
  async deleteClientOnboarding(onboardingId: string): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl(`client-onboarding/${onboardingId}`),
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) throw Error();
  }
}
